import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from '@theme/utilities';
import {
  getScrollTop,
  getScrollEventTarget,
  getIntersectionRoot,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */
class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /** @type {EventTarget | null} */
  #scrollContainer = null;

  /**
   * Whether the header has been scrolled offscreen,
   * when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document,
   * when sticky behavior is 'scroll-up'
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation,
   * when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * Keeps the global --header-height custom property up to date.
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);

    document.body.style.setProperty(
      '--header-height',
      `${roundedHeaderHeight}px`
    );

    if (
      this.#menuDrawerHiddenWidth &&
      window.innerWidth > this.#menuDrawerHiddenWidth
    ) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport.
   *
   * @param {Boolean} alwaysSticky
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
      root: getIntersectionRoot(),
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';

        if (this.dataset.themeColor) {
          changeMetaThemeColor(this.dataset.themeColor);
        }
      } else {
        this.#offscreen =
          !isIntersecting ||
          this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * @param {boolean} hideMenu
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;

      const overflowList = this.querySelector('overflow-list');

      if (overflowList) {
        overflowList.removeAttribute('minimum-reached');
      }
    }

    setHeaderMenuStyle();
  }

  /**
   * Rebind scroll behavior when the viewport crosses
   * the theme's responsive breakpoint.
   */
  #handleBreakpointChange = () => {
    const stickyMode = this.getAttribute('sticky');

    if (!stickyMode) return;

    if (this.#scrollContainer) {
      this.#scrollContainer.removeEventListener(
        'scroll',
        this.#handleWindowScroll
      );

      this.#scrollContainer = getScrollEventTarget();

      this.#scrollContainer.addEventListener(
        'scroll',
        this.#handleWindowScroll
      );
    }

    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;

    this.#observeStickyPosition(
      stickyMode === 'always'
    );
  };

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');

    if (
      !this.#offscreen &&
      stickyMode !== 'always'
    ) {
      return;
    }

    const scrollTop = getScrollTop();
    const headerTop = this.getBoundingClientRect().top;

    const isScrollingUp =
      scrollTop < this.#lastScrollTop;

    const isAtTop =
      headerTop >= 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;

      return;
    }

    if (isScrollingUp) {
      if (isAtTop) {
        this.#offscreen = false;

        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (
      this.dataset.stickyState === 'active'
    ) {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();

    this.#resizeObserver.observe(this);

    this.addEventListener(
      'overflowMinimum',
      this.#handleOverflowMinimum
    );

    const stickyMode =
      this.getAttribute('sticky');

    if (stickyMode) {
      this.#observeStickyPosition(
        stickyMode === 'always'
      );

      if (
        stickyMode === 'scroll-up' ||
        stickyMode === 'always'
      ) {
        this.#scrollContainer =
          getScrollEventTarget();

        this.#scrollContainer.addEventListener(
          'scroll',
          this.#handleWindowScroll
        );
      }

      scrollContainerMediaQuery.addEventListener(
        'change',
        this.#handleBreakpointChange
      );
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#resizeObserver.disconnect();

    this.#intersectionObserver?.disconnect();

    this.removeEventListener(
      'overflowMinimum',
      this.#handleOverflowMinimum
    );

    scrollContainerMediaQuery.removeEventListener(
      'change',
      this.#handleBreakpointChange
    );

    this.#scrollContainer?.removeEventListener(
      'scroll',
      this.#handleWindowScroll
    );

    this.#scrollContainer = null;

    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(
        this.#scrollRafId
      );

      this.#scrollRafId = null;
    }

    document.body.style.setProperty(
      '--header-height',
      '0px'
    );
  }
}

if (
  !customElements.get(
    'header-component'
  )
) {
  customElements.define(
    'header-component',
    HeaderComponent
  );
}


/* ==========================================================
   OG HEATWAVE SHOP-ONLY PROMO BAR
   ========================================================== */

/**
 * Normalize URL paths so "/collections/all"
 * and "/collections/all/" are treated the same.
 *
 * @param {string} path
 */
function normalizeOGPath(path) {
  if (!path) return '/';

  if (path === '/') {
    return '/';
  }

  return path.replace(/\/+$/, '');
}


/**
 * Determine whether the visitor is currently
 * on the exact page linked by the SHOP menu item.
 */
function isOGHeatwaveShopPage() {
  const currentPath =
    normalizeOGPath(
      window.location.pathname
    );

  /*
   * Find the actual "Shop" navigation link.
   *
   * This means we do NOT need to hard-code
   * /collections/all or another Shopify URL.
   */
  const links =
    Array.from(
      document.querySelectorAll('a[href]')
    );

  const shopLink =
    links.find((link) => {
      const text =
        link.textContent
          ?.trim()
          .toLowerCase();

      return text === 'shop';
    });

  if (!shopLink) {
    /*
     * Fail safely:
     * no Shop link = no promo.
     */
    return false;
  }

  try {
    const shopURL =
      new URL(
        shopLink.href,
        window.location.origin
      );

    const shopPath =
      normalizeOGPath(
        shopURL.pathname
      );

    return (
      currentPath ===
      shopPath
    );
  } catch (error) {
    console.warn(
      '[OG Heatwave] Could not determine Shop URL:',
      error
    );

    return false;
  }
}


/**
 * Add OG Heatwave promo under the red header.
 *
 * IMPORTANT:
 * Only appears on the page linked by "Shop".
 */
function addOGHeatwaveHeaderPromo(
  header,
  headerGroup
) {
  if (!header || !headerGroup) {
    return;
  }

  /*
   * =========================================
   * SHOP PAGE ONLY
   * =========================================
   */

  if (!isOGHeatwaveShopPage()) {
    /*
     * Helpful in Shopify theme editor / live
     * navigation if an old promo is present.
     */
    document
      .getElementById(
        'og-header-promo'
      )
      ?.remove();

    return;
  }

  /*
   * Prevent duplicate promo bars.
   */
  if (
    document.getElementById(
      'og-header-promo'
    )
  ) {
    return;
  }

  /*
   * Create promo bar.
   */
  const promo =
    document.createElement('div');

  promo.id =
    'og-header-promo';

  promo.innerHTML = `
    <div class="og-header-promo-inner">

      <div class="og-header-promo-deal">

        <span class="og-header-promo-icon">
          🔥
        </span>

        <strong>
          STOCK UP &amp; SAVE
        </strong>

        <span class="og-header-promo-description">
          Buy 2+ bottles for
          <b>$10 each</b>
        </span>

      </div>


      <span class="og-header-promo-divider"></span>


      <div class="og-header-promo-shipping">

        <span class="og-header-promo-icon">
          🚚
        </span>

        <strong>
          FREE SHIPPING
        </strong>

        <span class="og-header-promo-shipping-copy">
          on orders $50+
        </span>

      </div>

    </div>
  `;

  /*
   * Insert directly below the red header.
   */
  header.insertAdjacentElement(
    'afterend',
    promo
  );


  /*
   * =========================================
   * STYLES
   * =========================================
   */

  if (
    document.getElementById(
      'og-header-promo-styles'
    )
  ) {
    return;
  }

  const style =
    document.createElement('style');

  style.id =
    'og-header-promo-styles';

  style.textContent = `

    /* =================================
       SHOP PROMO BAR
       ================================= */

    #og-header-promo {
      width: 100%;

      box-sizing:
        border-box;

      display: flex;

      align-items:
        center;

      justify-content:
        center;

      margin: 0;

      padding:
        5px 16px;

      background:
        #ffffff;

      border-bottom:
        1px solid
        rgba(
          75,
          0,
          0,
          0.11
        );

      position:
        relative;

      z-index: 3;
    }


    /* =================================
       INNER CONTENT
       ================================= */

    .og-header-promo-inner {
      width:
        fit-content;

      max-width:
        100%;

      box-sizing:
        border-box;

      display:
        flex;

      align-items:
        center;

      justify-content:
        center;

      gap: 15px;

      margin:
        0 auto;

      padding:
        3px 5px;

      color:
        #4b0000;

      font-size:
        clamp(
          10.5px,
          0.78vw,
          12.5px
        );

      line-height:
        1.2;

      text-align:
        center;
    }


    /* =================================
       OFFER SECTIONS
       ================================= */

    .og-header-promo-deal,
    .og-header-promo-shipping {
      display:
        flex;

      align-items:
        center;

      justify-content:
        center;

      gap: 5px;

      white-space:
        nowrap;
    }


    .og-header-promo-inner strong {
      font-weight: 800;

      letter-spacing:
        0.15px;
    }


    .og-header-promo-inner b {
      font-weight: 800;
    }


    .og-header-promo-icon {
      line-height: 1;
    }


    /* =================================
       DIVIDER
       ================================= */

    .og-header-promo-divider {
      display: block;

      width: 1px;

      height: 17px;

      flex-shrink: 0;

      background:
        rgba(
          75,
          0,
          0,
          0.18
        );
    }


    /* =================================
       TABLET
       ================================= */

    @media screen and
    (max-width: 989px) {

      #og-header-promo {
        padding:
          5px 12px;
      }

      .og-header-promo-inner {
        gap: 10px;

        font-size:
          10.5px;
      }

      .og-header-promo-deal,
      .og-header-promo-shipping {
        gap: 4px;
      }

    }


    /* =================================
       MOBILE
       ================================= */

    @media screen and
    (max-width: 749px) {

      #og-header-promo {
        padding:
          5px 8px;
      }

      /*
       * Stack the two offers on mobile so neither
       * message gets cut off.
       */
      .og-header-promo-inner {
        width: 100%;

        flex-direction:
          column;

        gap: 3px;

        padding:
          2px 0;

        font-size:
          10px;
      }

      .og-header-promo-deal,
      .og-header-promo-shipping {
        width: 100%;

        gap: 3px;

        flex-wrap:
          wrap;

        white-space:
          normal;

        line-height:
          1.2;
      }

      .og-header-promo-description {
        font-size:
          9.5px;
      }

      /*
       * Keep the full free-shipping threshold visible.
       */
      .og-header-promo-shipping-copy {
        display: inline;

        font-size:
          9.5px;
      }

      /*
       * Horizontal divider between the two mobile rows.
       */
      .og-header-promo-divider {
        width: 68%;

        height: 1px;

        margin:
          1px auto;

        background:
          rgba(
            75,
            0,
            0,
            0.12
          );
      }

    }


    /* =================================
       SMALL MOBILE
       ================================= */

    @media screen and
    (max-width: 480px) {

      .og-header-promo-inner {
        gap: 3px;

        font-size:
          9.5px;
      }

      /*
       * Convert:
       *
       * "Buy 2+ bottles for $10 each"
       *
       * into:
       *
       * "2+ = $10 each"
       *
       * while keeping:
       * "FREE SHIPPING on orders $50+"
       */
      .og-header-promo-description {
        font-size: 0;
      }

      .og-header-promo-description b {
        font-size:
          9.5px;
      }

      .og-header-promo-description b::before {
        content:
          "2+ = ";
      }

      .og-header-promo-shipping-copy {
        display: inline;

        font-size:
          9.5px;
      }

    }


    /* =================================
       VERY SMALL MOBILE
       ================================= */

    @media screen and
    (max-width: 360px) {

      #og-header-promo {
        padding:
          4px 5px;
      }

      .og-header-promo-inner {
        gap: 4px;

        font-size:
          8.8px;
      }

      .og-header-promo-divider {
        margin:
          0 1px;
      }

      /*
       * Remove emoji only on
       * extremely small phones.
       */
      .og-header-promo-icon {
        display: none;
      }

    }

  `;

  document.head.appendChild(
    style
  );
}


/* ==========================================================
   HEADER INITIALIZATION
   ========================================================== */

onDocumentLoaded(() => {
  const header =
    document.querySelector(
      'header-component'
    );

  const headerGroup =
    document.querySelector(
      '#header-group'
    );


  /*
   * OG HEATWAVE:
   * Promo appears ONLY on whatever
   * page your "Shop" navigation
   * button points to.
   */
  addOGHeatwaveHeaderPromo(
    header,
    headerGroup
  );


  /*
   * Existing Shopify header-group
   * resize logic.
   */
  if (headerGroup) {
    const resizeObserver =
      new ResizeObserver(
        (entries) => {

          const headerGroupHeight =
            entries.reduce(
              (
                totalHeight,
                entry
              ) => {

                if (
                  entry.target !==
                    header ||
                  (
                    header?.hasAttribute(
                      'transparent'
                    ) &&
                    header.parentElement
                      ?.nextElementSibling
                  )
                ) {
                  return (
                    totalHeight +
                    (
                      entry
                        .borderBoxSize[0]
                        ?.blockSize ??
                      0
                    )
                  );
                }

                return totalHeight;
              },
              0
            );

          const roundedHeaderGroupHeight =
            Math.round(
              headerGroupHeight
            );

          document.body.style.setProperty(
            '--header-group-height',
            `${roundedHeaderGroupHeight}px`
          );
        }
      );


    if (
      header instanceof
      HTMLElement
    ) {
      resizeObserver.observe(
        header
      );
    }


    /*
     * Observe all children of
     * the header group.
     */
    const children =
      headerGroup.children;

    for (
      let i = 0;
      i < children.length;
      i++
    ) {
      const element =
        children[i];

      if (
        element instanceof
        HTMLElement
      ) {
        resizeObserver.observe(
          element
        );
      }
    }


    /*
     * Watch for dynamically added
     * header children.
     */
    const mutationObserver =
      new MutationObserver(
        (mutations) => {

          for (
            const mutation
            of mutations
          ) {
            if (
              mutation.type ===
              'childList'
            ) {

              const children =
                headerGroup.children;

              for (
                let i = 0;
                i < children.length;
                i++
              ) {
                const element =
                  children[i];

                if (
                  element instanceof
                  HTMLElement
                ) {
                  resizeObserver.observe(
                    element
                  );
                }
              }
            }
          }
        }
      );

    mutationObserver.observe(
      headerGroup,
      {
        childList: true,
      }
    );
  }
});